import { AppliedDataFilter } from '@/components/filters/FilterButton';
import {
  PayoutTransactionFilterName,
  PayoutTransactionsFilter,
  transactionSortOptions,
} from '@/components/Payouts/PayoutTransactionsFilter';
import {
  PayoutTransactionTable,
  PayoutTransactionTableColumnName,
} from '@/components/Payouts/PayoutTransactionTable';
import {
  NegativePayeeTotal,
  ProjectTransactionButton,
  ProjectTransactionsButtonBar,
} from '@/components/Payouts/ProjectTransactionsButtonBar';
import { ProjectTransactionsLoading } from '@/components/Payouts/ProjectTransactionsLoading';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetPayoutTransactionsQuery } from '@/services/payouts';
import { Alert, Box, Pagination } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DataFilterOperation,
  getProjectTransactionsQuery,
  PaginationElement,
  PayoutTransactionLevel,
  ProjectTransactionSort,
} from 'types';

import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';

export interface FilterablePayoutTransactionTableProps {
  lockedQueryFilters?: getProjectTransactionsQuery;
  hideColumns?: PayoutTransactionTableColumnName[];
  hideFilters?: PayoutTransactionFilterName[];
  hideSort?: ProjectTransactionSort[];
  enabledButtons?: ProjectTransactionButton[];
  hideSummary?: boolean;
  pagination?: any;
  showExpandedRow?: boolean;
  isCompact?: boolean;
  showContextMenu?: boolean;
  showPagination?: boolean;
  disableSelection?: boolean;
  defaultSort?: ProjectTransactionSort;
  searchPlaceholder?: string;
  transactionLevel?: PayoutTransactionLevel;
}

const defaultPagination = {
  count: 0,
  page: 1,
  limit: 500,
};

export const FilterablePayoutTransactionTable = ({
  lockedQueryFilters,
  hideColumns,
  hideFilters,
  hideSort,
  enabledButtons,
  hideSummary = false,
  showExpandedRow = false,
  showContextMenu = false,
  isCompact = false,
  showPagination = false,
  disableSelection = false,
  defaultSort = 'eventDateAsc',
  searchPlaceholder,
  transactionLevel,
}: FilterablePayoutTransactionTableProps) => {
  const [appliedFilters, setAppliedFilters] = useState<AppliedDataFilter[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState<PaginationElement>(defaultPagination);
  const [requestedPage, setRequestedPage] = useState<{ queryKey: string; page: number }>({ queryKey: '', page: 1 });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const advancedFilters = searchParams.get('advancedFilters') ?? '';
  const searchTerm = searchParams.get('search') || undefined;
  const sort = (searchParams.get('sort') as ProjectTransactionSort | null) || defaultSort;
  const appliedSort = useMemo(
    () => (sort ? transactionSortOptions.find((opt) => opt.id === sort) : transactionSortOptions[0]),
    [sort],
  );
  const query = useMemo(
    () => ({
      ...(lockedQueryFilters ? lockedQueryFilters : {}),
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(advancedFilters ? { advancedFilters: encodeURIComponent(advancedFilters) as any } : {}),
    }),
    [advancedFilters, lockedQueryFilters, searchTerm],
  );

  // The export covers every page, so it carries the sort but never page or limit.
  const exportQuery = useMemo(
    () => ({ ...query, ...(appliedSort ? { sort: appliedSort.id } : {}) }),
    [query, appliedSort],
  );

  // The page is tied to the query that requested it, so any query change falls back to page 1
  // without an effect that would first fire a request for a page that no longer exists.
  const queryKey = JSON.stringify([query, appliedSort?.id]);
  const page = requestedPage.queryKey === queryKey ? requestedPage.page : 1;

  const queryArgs = useMemo(
    () => ({
      ...query,
      ...(showPagination ? { page: `${page}`, limit: `${pagination.limit}` } : {}),
      ...(appliedSort ? { sort: appliedSort.id } : {}),
    }),
    [query, showPagination, page, pagination.limit, appliedSort],
  );

  const {
    data,
    isLoading: isTransactionsLoading,
    isFetching: isTransactionsFetching,
  } = useGetPayoutTransactionsQuery(queryArgs);
  const { transactions } = data ? data : { transactions: [] };

  const handleRowSelected = useCallback(
    (ids: string[], selected: boolean) => {
      if (selected) {
        setSelectedIds([...new Set([...selectedIds, ...ids])]);
      } else {
        setSelectedIds(selectedIds.filter((selectedId) => !ids.includes(selectedId)));
      }
    },
    [selectedIds, setSelectedIds],
  );

  useEffect(() => {
    if (data?.pagination) {
      // The echoed page is left out: a late response must not overwrite a page reset.
      const { page: _echoedPage, ...totals } = data.pagination;
      setPagination((current) => ({ ...current, ...totals }));
    }
  }, [data?.pagination]);

  const onPageChange = useCallback((newPage: number) => setRequestedPage({ queryKey, page: newPage }), [queryKey]);

  useEffect(() => {
    setSelectedIds([]);
  }, [queryArgs]);

  // Stable so the button bar's action callbacks can depend on it without being rebuilt every render.
  const resetSelections = useCallback(() => setSelectedIds([]), []);

  const handleSortSelection = useCallback(
    (sortSelection: { id: string; label: string }) => {
      searchParams.set('sort', sortSelection.id);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const totalPayoutAmount = useMemo(
    () => pagination?.totalAmount ?? transactions?.reduce((acc, transaction) => acc + transaction.amount, 0),
    [transactions, pagination?.totalAmount],
  );

  const payeesWithNegativeTotals = useMemo((): NegativePayeeTotal[] => {
    const payeeTotals = transactions?.reduce(
      (acc, transaction) => {
        const payeeId = transaction.payeeId;
        if (!payeeId) return acc;
        if (!acc[payeeId]) {
          acc[payeeId] = {
            total: 0,
            batchId: transaction.batchId?.toString() || '',
            payeeName: transaction.payee.name,
          };
        }
        acc[payeeId].total += transaction.amount;
        return acc;
      },
      {} as Record<string, { total: number; batchId: string; payeeName: string }>,
    );
    const negativePayeeTotals: NegativePayeeTotal[] = [];
    for (const payeeId in payeeTotals) {
      // Mirrors server-side roundTo function: totals within $0.01 of zero are treated as $0, not negative.
      if (payeeTotals[payeeId].total <= -0.01) {
        negativePayeeTotals.push({
          payeeId,
          payeeName: payeeTotals[payeeId].payeeName,
          total: payeeTotals[payeeId].total,
          batchId: payeeTotals[payeeId].batchId,
        });
      }
    }
    return negativePayeeTotals;
  }, [transactions]);

  /**
   * The payee filter carries a name while creating a transaction needs the organization alias, so
   * the alias is read off the rows the filter already narrowed rather than looked up again.
   *
   * Two things have to hold before trusting them. The filter must be an Equal: payeeName also
   * offers NotEqual, where one selected value *excludes* an organization and leaves every other one
   * in the result — the rows would then be an arbitrary payee. And the rows must agree, because
   * payee names are not unique, so even an Equal on a name can span organizations.
   */
  const defaultPayee = useMemo(() => {
    const isFilteredToOnePayeeName = appliedFilters.some(
      (filter) =>
        filter.id === 'payeeName' &&
        filter.operation === DataFilterOperation.Equal &&
        filter.selectedValues?.length === 1,
    );
    if (!isFilteredToOnePayeeName || !transactions.length) return undefined;

    const { payee } = transactions[0];
    const isOneOrganization = transactions.every(
      (transaction) => transaction.payee?.externalId === payee?.externalId,
    );

    return isOneOrganization && payee?.externalId
      ? { value: payee.externalId, label: `${payee.name} (${payee.externalId})` }
      : undefined;
  }, [appliedFilters, transactions]);

  const totalPayees = useMemo(() => {
    if (pagination?.totalPayees) return pagination.totalPayees;
    const payeeIds = transactions?.map((transaction) => transaction.payeeId);
    return new Set(payeeIds).size;
  }, [pagination.totalPayees, transactions]);

  return (
    <Box gap="md" background="primary" radius="md" padding="0">
      {!appliedFilters.some((f) => f.selectedValues?.length) &&
        !searchTerm &&
        payeesWithNegativeTotals.length > 0 &&
        enabledButtons?.includes('approve') && (
          <Alert
            variant="warning"
            title="Batch contains payees with negative totals"
            message="One or more payees have a negative net total. Use the Recalculate Batch button to resolve this before approving."
            hasIcon
          />
        )}
      <ProjectTransactionsButtonBar
        buttons={enabledButtons}
        query={exportQuery}
        selectedIds={selectedIds}
        resetSelections={resetSelections}
        transactions={transactions}
        payeesWithNegativeTotals={payeesWithNegativeTotals}
        transactionLevel={transactionLevel}
        defaultPayee={defaultPayee}
      />
      <PayoutTransactionsFilter
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        searchTerm={searchTerm}
        advancedFilters={advancedFilters}
        handleSortSelection={handleSortSelection}
        appliedSort={appliedSort}
        hideSortOptions={hideSort}
        hideFilterOptions={hideFilters ?? []}
        searchPlaceholder={searchPlaceholder}
      />
      {!hideSummary && (
        <Box padding="md 0" alignItems="flex-end">
          <Box direction="row" childGap="md" padding="0 md">
            <Box direction="column" alignItems="flex-end">
              <RequirePalmettoFinanceUser>
                <Box fontSize="lg" fontWeight="medium" padding="0 0 xs 0">
                  {totalPayees}
                </Box>
                Payees
              </RequirePalmettoFinanceUser>
            </Box>
            <Box direction="column" alignItems="flex-end" padding="0 md">
              <Box fontSize="lg" fontWeight="medium" padding="0 0 xs 0">
                {pagination?.count ?? transactions?.length}
              </Box>
              Transactions
            </Box>
            <Box direction="column" alignItems="flex-end" padding="0 0 0 md">
              <Box fontSize="lg" fontWeight="medium" padding="0 0 xs 0">
                {currencyFormatter.format(totalPayoutAmount)}
              </Box>
              Total Payout Amount
            </Box>
          </Box>
        </Box>
      )}
      {isTransactionsLoading || isTransactionsFetching ? (
        <ProjectTransactionsLoading />
      ) : (
        <PayoutTransactionTable
          transactions={transactions}
          handleRowSelected={handleRowSelected}
          selectedIds={selectedIds}
          hideColumns={hideColumns ?? []}
          showExpandedRow={showExpandedRow}
          isCompact={isCompact}
          showContextMenu={showContextMenu}
          disableSelection={disableSelection}
          transactionLevel={transactionLevel}
        />
      )}
      <Box width="100">
        {showPagination &&
        pagination?.count &&
        pagination.limit &&
        Math.ceil(pagination.count / pagination.limit) > 1 ? (
          <Pagination
            activePage={page}
            arePagesVisible
            itemsPerPage={pagination.limit}
            numberOfPagesDisplayed={9}
            onChange={onPageChange}
            totalItemsCount={pagination.count}
            isCompact
          />
        ) : null}
      </Box>
    </Box>
  );
};
